import React, {useEffect} from "react";
import styles from "./AnalyticsTab.module.css";
import {useAppDispatch, useAppSelector} from "../../../../app/hooks";
import {MenuItem, updateSelectedMenuItem} from "../../platform/platformSlice";
import {
    analyticsBotOptionsSelector,
    analyticsBotOptionsRequestStatusSelector,
    analyticsRequestSelector,
    analyticsRequestStatusSelector,
    analyticsResponseSelector,
    fetchAnalyticsBotOptions,
    fetchTranscriptAnalytics,
    resetAnalyticsRequest,
    updateAnalyticsRequest
} from "../analyticsSlice";
import {ConversationChannel} from "../../../../api/generated";
import moment from "moment";
import {RequestStatus} from "../../../../api/apiConfiguration";
import {Loader} from "../../../elements/loader/Loader";
import {showErrorToastNotification} from "../../../elements/toastNotification/toastNotificationSlice";
import {AnalyticsCharts} from "./components/AnalyticsCharts/AnalyticsCharts";
import {AnalyticsHeader} from "./header/AnalyticsHeader";
import {useRequestErrorToast} from "../../../../hooks/useRequestErrorToast";

export const AnalyticsTab: React.FC = () => {
    const dispatch = useAppDispatch();
    const request = useAppSelector(analyticsRequestSelector);
    const response = useAppSelector(analyticsResponseSelector);
    const botOptions = useAppSelector(analyticsBotOptionsSelector);
    const status = useAppSelector(analyticsRequestStatusSelector);

    const {
        startDate,
        endDate,
        publicBotIdList,
        conversationChannelList
    } = request;

    useEffect(() => {
        dispatch(updateSelectedMenuItem(MenuItem.ANALYTICS));
        dispatch(fetchAnalyticsBotOptions());
    }, [dispatch]);

    useRequestErrorToast(useAppSelector(analyticsBotOptionsRequestStatusSelector),
        "Failed to load the assistant filter");

    useEffect(() => {
        dispatch(fetchTranscriptAnalytics());
    }, [dispatch, startDate, endDate, publicBotIdList, conversationChannelList]);

    useEffect(() => {
        if (status === RequestStatus.REJECTED) {
            dispatch(showErrorToastNotification({message: "Failed to load analytics"}));
        }
    }, [dispatch, status]);

    const handleDateRangeChange = (start: moment.Moment, end: moment.Moment) => {
        dispatch(updateAnalyticsRequest({startDate: start.toISOString(), endDate: end.toISOString()}));
    };

    const isLoading = status === RequestStatus.PENDING;

    if (isLoading) {
        return <div className={styles.wrapper}>
            <Loader/>
        </div>;
    }

    return <div className={styles.wrapper}>
        <div className={styles.header}>
            <AnalyticsHeader
                botOptions={botOptions}
                request={request}
                onAssistantChange={(publicBotIds) => dispatch(updateAnalyticsRequest({publicBotIdList: publicBotIds}))}
                onChannelChange={(channels: ConversationChannel[]) => dispatch(updateAnalyticsRequest({conversationChannelList: channels}))}
                onDateRangeChange={handleDateRangeChange}
                onReset={() => dispatch(resetAnalyticsRequest())}
            />
        </div>
        <div className={styles.content}>
            <AnalyticsCharts
                overview={response?.overview}
                dailyTrend={response?.dailyTrend || []}
                newVsReturning={response?.newVsReturningUsers}
                messageTypeBreakdown={response?.messageTypeBreakdown || []}
                toolUsageBreakdown={response?.toolUsageBreakdown || []}
                channelBreakdown={response?.channelBreakdown || []}
            />
        </div>
    </div>;
};
